<template>
  <div  class="manager_dashboard">
    <user-menu />
    <div class="wrapper">
      <EnterpriseAdminSidebarMenu  :show_menu="false" :show_back_button="true" back_link="enterpriseadminreports"/>
      <div class="content-wrapper padding-left-165">
        <div class="container-wrapper center">
          <div class="modal-custom" v-show="showOverlay">
            <div class="loader-img-center">
              <i class="fa fa-spinner fa-spin"></i> Please wait...
            </div>
          </div>
          <div class="comparison-page">
            <div class="comparison-header">
              <div>
								<h1 class="reports-title">Mentee Comparison</h1>
              </div>
            </div>

            <div class="comparison-filters-export">
              <div class="comparison-filters">
                <div class="search-input-wrapper">
                  <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>
                  <input
                    v-model="searchQuery"
                    class="comparison-search"
                    type="text"
                    placeholder="Search mentees..."
                  />
                </div>
                <select v-model="selectedPathway" @change="onFilterChange" class="filter-select">
                  <option v-for="option in pathwayOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <select v-model="selectedLocation" @change="onFilterChange" class="filter-select">
                  <option v-for="option in locationOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <select v-model="selectedGroup" @change="onFilterChange" class="filter-select">
                  <option v-for="option in groupOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
              </div>
              <button class="export-button" type="button" @click="downFunc()"><svg xmlns="http://www.w3.org/2000/svg" style="padding-top: 3px; vertical-align: top;" width="13" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download w-4 h-4" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:329:14" data-component-name="Download"><path d="M12 15V3"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="m7 10 5 5 5-5"></path></svg> Export CSV</button>
            </div>

            <div v-if="loading" class="comparison-cards skeleton-cards">
              <div class="summary-card summary-card-skeleton" v-for="i in 4" :key="i">
                <PuSkeleton height="18px" :count="1" width="120px"></PuSkeleton>
                <PuSkeleton height="34px" :count="1" width="80px" style="margin-top: 12px;"></PuSkeleton>
              </div>
            </div>
            <div v-else class="comparison-cards">
              <div class="summary-card summary-card-blue">
                <div class="summary-label">
                  <div v-html="locationSvg" />
                  <div style="padding-top: 3px;">Locations</div>
                </div>
                <div class="summary-value" style="color: #00ACEE;">{{ locationCount }}</div>
              </div>
              <div class="summary-card summary-card-lightblue">
                <div class="summary-label">
                  <div v-html="menteeSvg" />
                  <div style="padding-top: 3px;">Total Mentees</div>
                </div>
                <div class="summary-value" style="color: #0079F2;">{{ menteeCount }}</div>
              </div>
              <div class="summary-card summary-card-green">
                <div class="summary-label">
                  <div v-html="competencySvg" />
                  <div style="padding-top: 3px;">Avg Competency</div>
                </div>
                <div class="summary-value" style="color: #009118;">{{ avgCompetency }}%</div>
              </div>
              <div class="summary-card summary-card-purple">
                <div class="summary-label">
                  <div v-html="courseCompletionSvg" />
                  <div style="padding-top: 3px;">Avg Course Completion</div>
                </div>
                <div class="summary-value" style="color: #795EFF;">{{ avgCourseCompletion }}%</div>
              </div>
            </div>

            <div class="mentee-detail-header">
              <div class="mentee-detail-title">
                Mentee Detail <span>({{ menteeCount }})</span>
              </div>
              <div class="mentee-detail-action" @click="toggleShowAll">{{ showAllRows ? 'See Less' : 'See All' }}</div>
            </div>

            <div class="comparison-table">
              <div v-if="loading" class="table-skeleton">
                <div v-for="i in 6" :key="i" class="table-skeleton-row">
                  <PuSkeleton height="20px" :count="1" width="100%"></PuSkeleton>
                </div>
              </div>
              <vue-good-table
                v-else
                :columns="columns"
                :rows="filteredRows"
                :search-options="{ enabled: false }"
                :pagination-options="showAllRows ? { enabled: false } : paginationOptions"
                :class="{ 'hide-pagination': showAllRows }"
                @on-per-page-change="onPerPageChange"
                style-class="vgt-table striped bordered"
              >
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'competencyPercent'">
                    <div class="competency-bar-container">
                      <div class="competency-bar-wrapper">
                        <div class="competency-bar-background">
                          <div 
                            class="competency-bar-fill" 
                            :class="getCompetencyClass(props.row.competencyPercent)"
                            :style="{ width: props.row.competencyPercent + '%' }">
                          </div>
                        </div>
                      </div>
                      <div class="competency-percentage" :class="getCompetencyClassForColor(props.row.competencyPercent)">{{ props.row.competencyPercent }}%</div>
                    </div>
                  </span>
                  <span v-else-if="props.column.field === 'avgCompetencyPercent'">
                    {{ props.row.totalCompetencyEarned }} / {{ props.row.totalCompetency }}
                  </span>
                  <span v-else-if="props.column.field === 'avgReps'">
                    {{ props.row.totalRepsAdded }} / {{ props.row.totalReps }}
                  </span>
                  <span v-else-if="props.column.field === 'avgCourses'">
                    {{ props.row.totalCoursesCompleted }} / {{ props.row.totalCourses }}
                  </span>
                  <span v-else-if="props.column.field === 'avgCerts'">
                    {{ props.row.totalCertsCompleted }} / {{ props.row.totalCerts }}
                  </span>
                  <span v-else-if="props.column.field === 'accountName'">
                    <svg xmlns="http://www.w3.org/2000/svg" style="padding-top: 1px; vertical-align: top;" width="14" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3 h-3 text-[#00ACEE]" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:155:10" data-component-name="MapPin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg> {{ props.row.accountName }}
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
								<template v-slot:emptystate>
									<div class="custom-no-data text-center">
										No matching mentees found!
									</div>
								</template>
              </vue-good-table>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue/dist/vue.esm';
import UserMenu from '../shared/user_menu.vue';
import axios from 'axios';
import router from '../../router/index.js';
import EnterpriseAdminSidebarMenu from '../shared/enterpriseadmin_sidebar_menu.vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';

export default {
    data() {
      return {
        loading: false,
        showOverlay: false,
        searchQuery: '',
        locationCount: 0,
        menteeCount: 0,
        avgCompetency: 0,
        avgCourseCompletion: 0,
        selectedPathway: '',
        selectedLocation: '',
        selectedGroup: '',
        pathwayOptions: [],
        locationOptions: [],
        groupOptions: [],
        showAllRows: false,
        columns: [
          { label: 'Mentee', field: 'menteeName', sortable: true, tdClass: 'font-weight-500' },
          { label: 'Location', field: 'accountName', sortable: true },
          { label: 'Pathway', field: 'pathwayName', sortable: true },
          { label: 'Competency %', field: 'competencyPercent', sortable: true, type: 'number', thClass: 'text-left', tdClass: 'text-left' },
          { label: 'Competencies', field: 'avgCompetencyPercent', sortable: true, type: 'number', thClass: 'text-left', tdClass: 'text-left' },
          { label: 'Reps', field: 'avgReps', sortable: true, type: 'number', thClass: 'text-left', tdClass: 'text-left' },
          { label: 'Courses', field: 'avgCourses', sortable: true, type: 'number', thClass: 'text-left', tdClass: 'text-left' },
          { label: 'Certs', field: 'avgCerts', sortable: true, type: 'number', thClass: 'text-left', tdClass: 'text-left' }
        ],
        paginationOptions: {
          enabled: true,
          perPage: 25,
          perPageDropdown: [25, 50, 100, 150, 200],
          dropdownAllowAll: false,
          nextLabel: 'Next',
          prevLabel: 'Previous',
          rowsPerPageLabel: 'Row count',
          mode: 'records',
					infoFn: (params) => `Showing ${params.firstRecordOnPage} to ${params.lastRecordOnPage} of ${params.totalRecords} results`
        },
        rows: [],
				locationSvg: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:351:18" data-component-name="MapPin"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>',
				menteeSvg: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:362:18" data-component-name="Users"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><path d="M16 3.128a4 4 0 0 1 0 7.744"></path><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><circle cx="9" cy="7" r="4"></circle></svg>',
				competencySvg: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-award w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:373:18" data-component-name="Award"><path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path><circle cx="12" cy="8" r="6"></circle></svg>',
				courseCompletionSvg: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-book-open w-3.5 h-3.5" aria-hidden="true" data-replit-metadata="artifacts/analytics-dashboard/src/components/reports/CompetencyComparisonReport.tsx:390:18" data-component-name="BookOpen"><path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path></svg>',
      }
    },
    components: {
      UserMenu,
      EnterpriseAdminSidebarMenu,
      VueGoodTable,
    },
    computed: {
      isReadOnly(){
        if (this.$store.state.enterprise_type === 'Readonly') {
          return true;
        } else {
          if (this.$store.state.switch_admin_enterprise_isreadonly) {
            return true;
          } else {
            return false;
          }
        }
      },
      filteredRows() {
        const dataSource = this.rows && this.rows.length > 0 ? this.rows : [];
        return dataSource.filter(row => {
          const searchTerm = this.searchQuery.toLowerCase().trim();
          const menteeNameMatch = !searchTerm || String(row.menteeName || '').toLowerCase().includes(searchTerm);
          return menteeNameMatch;
        });
      }
    },
    methods: {
			downFunc() {
				this.showOverlay = true;
				axios.get('/enterprise-admin/mentee-comparison-report-csv', {
					params: {
						pathway_id: this.selectedPathway, 
						account_id: this.selectedLocation, 
						search_group: this.selectedGroup, 
					}
				})
				.then(response => {
					this.showOverlay = false;
					var fileName = response.data;
					if (fileName) {
						var url = this.apiUrl + '/custom/csv-view/' + fileName + '/Mentee-Comparison-Report';
						window.location.href = url;
					}
				})
				.catch(error => {

				});
			},
      navigate(path) {
        router.push(path);
      },
      onFilterChange() {
        this.getMenteeComparisonData();
      },
      toggleShowAll() {
        this.showAllRows = !this.showAllRows;
      },
      onPerPageChange(params) {
        const selectedPerPage = params && (params.currentPerPage || params.currentPage || params.perPage);
        if (selectedPerPage) {
          this.paginationOptions.perPage = selectedPerPage;
          this.$store.dispatch('mentee_comparison_page_size', selectedPerPage);
        }
      },
      getCompetencyClassForColor(percent) {
        if (percent < 32) return 'competency-color-red';
        if (percent <= 65) return 'competency-color-yellow';
        return 'competency-color-green';
      },
      getCompetencyClass(percent) {
        if (percent < 32) return 'competency-red';
        if (percent <= 65) return 'competency-yellow';
        return 'competency-green';
      },
      getMenteeComparisonData() {
        this.loading = true;
				this.menteeCount = 0;
        axios('/enterprise-admin/mentee-comparison-report',
        {
          method: 'GET',
					params: {
						pathway_id: this.selectedPathway, 
						account_id: this.selectedLocation, 
						search_group: this.selectedGroup, 
					},
        })
        .then(response => {
          this.loading = false;
          this.rows = response.data.data || [];
          this.menteeCount = response.data.menteeCount;
          this.locationCount = response.data.accountCount;
          this.avgCompetency = response.data.avgCompetency;
          this.avgCourseCompletion = response.data.avgCourseCompletion;
        })
        .catch(() => {
          this.loading = false;
        });
      },
    },
    created: function () {
      const storedPageSize = this.$store.state.menteeComparisonPageSize;
      if (storedPageSize && Number.isInteger(storedPageSize)) {
        this.paginationOptions.perPage = storedPageSize;
      }
			this.loading = true;
			this.apiUrl = process.env.API_ENDPOINT;
			this.accountId = this.$route.params.accountId;
      this.getMenteeComparisonData();
			axios('/enterprise-admin/getAllLinkedLocations',
			{
				method: 'GET'
			})
			.then(response => {
				this.locationOptions.push({ text:'All Locations', value: '' });
				for (var m = 0; m < response.data.locations.length; m++) {
					this.locationOptions.push({ 
						text: response.data.locations[m].company_name, 
						value: response.data.locations[m].id 
					});
				}
			})
			.catch(() => {
			});
			axios('/enterprise-admin/groups/' + this.$store.state.user_accountid,
			{
				method: 'GET',
			})
			.then(response => {
				var groupsCount = response.data.groups.length;
				this.groupOptions.push({ text:'All Groups', value: '' });
				for (var m = 0; m < groupsCount; m++) {
						this.groupOptions.push({ 
								text: response.data.groups[m].text, 
								value: response.data.groups[m].value 
						});
				}
			})
			.catch(() => {
			});
			axios('/enterprise-admin/AllPathways',
			{
				method: 'GET',
        params: {
          accountId: this.$store.state.user_accountid, 
				},
			})
			.then(response => {
				this.pathwayOptions.push({ text:'All Pathways', value: '' });
				for (var m = 0; m < response.data.length; m++) {
						this.pathwayOptions.push({ 
								text: response.data[m].pathwayName, 
								value: response.data[m].id 
						});
				}
			})
			.catch(() => {
			});

    }
}
</script>
<style scoped>
  .back-btn-wrapper {
    width:100%;
    margin-left: 10px;
    margin-right: 0px;
  }
  .comparison-page {
    background: #fff;
    border-radius: 20px;
    padding: 10px 5px;
		padding-bottom: 40px;
  }
  .comparison-header {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 9px;
  }
  .reports-subtitle {
    margin: 8px 0 0;
    color: #6b7280;
    max-width: 760px;
  }
  .comparison-filters-export {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
  }
  .comparison-filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }
  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  .search-icon {
    position: absolute;
    left: 12px;
    width: 16px;
    height: 16px;
    pointer-events: none;
    color: #64748b;
  }
  .comparison-search {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 10px 14px 10px 38px;
    width: 220px;
    min-width: 180px;
		outline: none;
  }
  .filter-select {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 10px 12px;
    background: #fff;
    min-width: 160px;
    outline: none;
  }
  .export-button {
    background: #FFF;
    color: #002842;
    border: 1px solid #d1d5db;
    padding: 10px 22px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 500;
		font-size: 0.875rem;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .export-button:hover {
    background: #F9FAFB;
		cursor: pointer;
  }
  .comparison-cards {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 28px;
  }
  .summary-card {
    border-radius: 12px;
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border: 1px solid #e5e7eb;
    background: #ffffff;
  }
  .summary-label {
    color: #6b7280;
    font-size: 14px;
    margin-bottom: 0px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .summary-label i {
    font-size: 18px;
    display: inline-flex;
    align-items: center;
  }
  .mentee-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0px 0 0px;
    padding: 0 15px;
		min-height: 50px;
		background-color: color-mix(in oklab, #EDF0F3 30%, transparent);
		border: 1px solid #E0E6EB;
		border-radius: 12px;
		border-bottom: 0;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
  }
  .mentee-detail-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #627384;
  }
  .mentee-detail-title span {
    color: #627384;
    font-weight: 500;
    margin-left: 0px;
  }
  .mentee-detail-action {
    color: color-mix(in oklab, #00ACEE 80%, transparent);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
		padding: 5px 10px;
  }
  .mentee-detail-action:hover {
    text-decoration: none;
		background-color: color-mix(in oklab, #00ACEE 10%, transparent);
		border-radius: calc(0.625rem - 2px);
  }
  .summary-value {
    font-size: 1.5rem;
    font-weight: 700;
  }
  .summary-card-blue {
    border-left: 4px solid #00ACEE;
  }
  .summary-card-lightblue {
    border-left: 4px solid #0079F2;
  }
  .summary-card-green {
    border-left: 4px solid #009118;
  }
  .summary-card-purple {
    border-left: 4px solid #795EFF;
  }
  .table-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-bottom: 20px;
  }
  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
  }
  .filter-group label {
    font-size: 13px;
    color: #475569;
  }
  .filter-group select {
    border: 1px solid #d1d5db;
    border-radius: 10px;
    padding: 10px 12px;
    background: #fff;
  }
  .comparison-table {
    background: #ffffff;
    border-radius: 0px;
    padding: 0px;
    border: 0px solid #e5e7eb;
		border-top-left-radius: 0;
		border-top-right-radius: 0;
  }
  .competency-indicator {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
    vertical-align: middle;
  }
  .competency-red {
    background-color: #ee4200;
  }
  .competency-yellow {
    background-color: #f4d454;
  }
  .competency-green {
    background-color: #009118;
  }
  .competency-color-red {
    color: #ee4200;
  }
  .competency-color-yellow {
    color: #f4d454;
  }
  .competency-color-green {
    color: #009118;
  }
  .competency-bar-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .competency-bar-wrapper {
    flex: 1;
    min-width: 100px;
  }
  .competency-bar-background {
    width: 100%;
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
  }
  .competency-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
    min-width: 0px;
  }
  .competency-percentage {
    min-width: 40px;
    text-align: right;
    font-weight: 500;
  }
  .competency-percentage.competency-red {
    color: #ee4200;
  }
  .competency-percentage.competency-yellow {
    color: #f4d454;
  }
  .competency-percentage.competency-green {
    color: #009118;
  }
  .skeleton-loader-inline {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .skeleton-cards {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 28px;
  }
  .summary-card-skeleton {
    border-radius: 12px;
    padding: 18px 22px;
    min-height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: 1px solid #e5e7eb;
    background: #ffffff;
  }
  .table-skeleton {
    padding: 18px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }
  .table-skeleton-row {
    margin-bottom: 12px;
  }
  .table-skeleton-row:last-child {
    margin-bottom: 0;
  }
  .skeleton-line {
    height: 16px;
    background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
  }
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

.comparison-table >>> .vgt-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}

.comparison-table >>> .vgt-table thead th {
  background: none;
  background-color: #FFF;
  color: #627384;
  font-weight: normal;
  font-size: 0.875rem;
  padding: 9px 12px;
  border-right: 0 !important;
}
.comparison-table >>> .vgt-table.striped thead tr th {
  background-color: color-mix(in oklab, #EDF0F3 30%, transparent);
}
.comparison-table >>> .vgt-table.striped tbody tr {
  background: #FFF !important;
}

.comparison-table >>> .vgt-table tbody tr:hover, .comparison-table >>> .vgt-table thead tr:hover {
  background-color: color-mix(in oklab, #EDF0F3 50%, transparent) !important;
}

.comparison-table >>> .vgt-table tbody td {
  font-size: 0.875rem;
  padding: 8px 12px;
  color: #00233b;
  border-right: 0 !important;
}

.comparison-table >>> .vgt-wrap__footer {
  border-top: 0;
  padding: 14px 0 0;
  font-size: 13px;
  color: #6b7280;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
}
.hide-pagination >>> .vgt-wrap__footer {
  display: none !important;
}
.comparison-table >>> .vgt-wrap__footer .footer__row-count {
  display: block !important;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  color: #5f7f9b;
  font-size: 14px;
  font-weight: 500;
  border: 0;
  text-align: center;
}
.comparison-table >>> .vgt-wrap__footer .footer__row-count__label,
.comparison-table >>> .vgt-wrap__footer .footer__row-count__select {
  display: inline-block !important;
}
.comparison-table >>> .vgt-wrap__footer .footer__row-count__select {
  min-width: auto !important;
  padding: 8px 10px !important;
  border-radius: 8px !important;
  border: 1px solid #d1d5db !important;
  background: #fff !important;
  color: #374151 !important;
	outline: none;
	font-size: 14px;
	font-weight: 500;
	width: 60px;
}
.comparison-table >>> .vgt-wrap__footer .chevron {
  display: inline-block !important;
  color: #002F42 !important;
  font-size: 12px;
  vertical-align: middle;
}

.comparison-table >>> .vgt-wrap__footer .footer__navigation__page-btn {
  color: #7c8896;
  border: 1px solid #d8e0e7;
  background: #FFF;
  border-radius: 9px;
  min-width: 70px;
  height: 30px;
  font-size: 13px;
  font-weight: 500;
  padding: 0 10px;
}

.comparison-table >>> .vgt-wrap__footer .footer__navigation__page-btn:hover {
  color: #526071;
  background: #FFF;
}

.comparison-table >>> .vgt-wrap__footer .footer__row-count {
  display: block !important;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  color: #5f7f9b;
  font-size: 14px;
  font-weight: 500;
  border: 0;
  text-align: center;
}

.comparison-table >>> .vgt-wrap__footer .footer__row-count__label,
.comparison-table >>> .vgt-wrap__footer .footer__row-count__select {
  display: inline-block !important;
}
.comparison-table >>> .vgt-wrap__footer .footer__row-count__label {
	color: #002f42 !important;
	font-size: 14px;
	padding-top: 8px;
}
.comparison-table >>> .vgt-wrap__footer .footer__navigation {
  margin-left: 0px;
  gap: 10px;
}


.comparison-table >>> .vgt-global-search {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 14px;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.comparison-table >>> .vgt-global-search__input,
.comparison-table >>> .vgt-input {
  width: 420px;
  max-width: 100%;
  height: 36px;
  border: 1px solid #d8e0e7;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: #475569;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.comparison-table >>> .vgt-global-search__input {
    padding-left: 0px;
    border: 0;
    border-radius: 0;
    box-shadow: none;

}

.comparison-table >>> .vgt-global-search__input::placeholder,
.comparison-table >>> .vgt-input::placeholder {
  color: #7d8a99;
}

.comparison-table >>> .vgt-global-search__actions {
  display: none;
}

/* Hide sort icons until a column is actively sorted. */
.comparison-table >>> .vgt-table thead th.sortable .vgt-sortable:after {
  opacity: 0;
}

.comparison-table >>> .vgt-table thead th.sorting-asc .vgt-sortable:after,
.comparison-table >>> .vgt-table thead th.sorting-desc .vgt-sortable:after {
  opacity: 1;
}
.text-\[\#00ACEE\] {
    color: #00ACEE;
}
.reports-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
}
.font-weight-500 {
	font-weight: 500;
}
</style>
<style>
.magnifying-glass {
  display: none !important;
}
.vgt-inner-wrap {
  box-shadow: none !important;
}
.vgt-wrap__footer {
  border: 0 !important;
}
.vgt-table.bordered td, .vgt-table.bordered th {
  border: 0px;
  border-bottom: 1px solid #dcdfe6;
}
.vgt-wrap__footer .footer__navigation__page-btn span {
  font-size: 14px;
}
.vgt-wrap__footer .footer__navigation__page-btn {
  
}
.vgt-pull-right {
  width: 100%;
  padding: 0;
  margin: 0;
  text-align: right;
  display: flex;
}
.footer__navigation__page-info {
  margin: 0 !important;
  font-size: 14px;
  width: auto;
  color: #002f42 !important;
  flex-grow: 1;
  text-align: left;
  padding-top: 2px;
}
table.vgt-table td {
  color: #002F42 !important;
}
.vgt-wrap__footer .footer__row-count::after {
  display: inline-block !important;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #5f7f9b;
  content: '' !important;
  margin-left: 8px;

}
.comparison-page .vgt-inner-wrap {
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/enterpriseadmin/menteeComparisonReport.vue